import {
  EUserAccessPart,
  EWorkspaceProjectRole,
  IProjectsResponseDto,
  IUserDto,
  IUserProjectRole,
  IWorkspaceProjectRole
} from "@kuika/kuika-cl-model";
import React from "react";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaAvatar, { getIdentitySeed } from "../../../ui-kit/kuika-avatar/kuika-avatar";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaRoleBadge from "../../../ui-kit/kuika-role-badge/kuika-role-badge";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import "./project-definition-share.scss";

export interface IState {}

export interface IProps {
  withoutRole?: boolean;
  user: IUserDto;
  projectRoleList: IWorkspaceProjectRole[];
  removeSelectedUserToShare?: (user: IUserProjectRole) => void;
  projectRole?: IWorkspaceProjectRole | undefined;
  updateSelectedUsersToShareUserRole?: (user: IUserProjectRole, roleId: string) => void;
  projectToEdit: IProjectsResponseDto;
  /**
   * Optional `data-testid` for this row. The same component renders both a search-result
   * candidate and an already-shared member, so the id is supplied by the caller (derived
   * from the user) rather than hardcoded here.
   */
  testId?: string;
}

class ProjectDefinitionShareUser extends React.PureComponent<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {};
  }

  // Roles, then "Remove access" in its own (divided, danger) group. `showSelectedIcon` because
  // the kit marks the selected row only on onClick-less items otherwise.
  getRoleMenuOptions = (): KuikaDropdownMenuItemType[] => {
    const roleOptions: KuikaDropdownMenuItemType[] = (this.props.projectRoleList ?? [])
      .filter((role: IWorkspaceProjectRole) => role.enumId !== EWorkspaceProjectRole.ProjectOwner)
      .map((role: IWorkspaceProjectRole): KuikaDropdownMenuItemType => ({
        key: role.id,
        label: role.name,
        group: 1,
        showSelectedIcon: true,
        onClick: () => {
          if (this.props.updateSelectedUsersToShareUserRole) {
            this.props.updateSelectedUsersToShareUserRole(
              {
                userId: this.props.user.id,
                projectRoleId: role.id
              },
              role.id
            );
            this.forceUpdate();
          }
        }
      }));

    return [
      ...roleOptions,
      {
        key: "delete",
        label: "Remove access",
        group: 2,
        danger: true,
        icon: <KuikaSvg name="trash" width={12} height={12} />,
        onClick: () => {
          if (this.props.removeSelectedUserToShare) {
            this.props.removeSelectedUserToShare({
              userId: this.props.user.id,
              projectRoleId: ""
            });
          }
        }
      }
    ];
  };

  getUserRole = (user: IUserDto) => {
    const userRole = this.props.projectRoleList?.find((role: IWorkspaceProjectRole) => role.id === user.id);
    return userRole?.name;
  };

  render() {
    const user = this.props.user;
    const hasAccessChangeRole = KMainFunctions.hasUserAccessInWorkspace(EUserAccessPart.EditProjectRole);
    const hasAccessEditProjectRoleInProject = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.EditProjectRole,
      this.props.projectToEdit.projectAccess
    );
    const isOwner: boolean = this.props.projectRole?.enumId === EWorkspaceProjectRole.ProjectOwner;
    const canEditRole: boolean =
      !this.props.withoutRole && !isOwner && (hasAccessChangeRole || hasAccessEditProjectRoleInProject);
    const selectedRoleId: string | undefined = this.props.projectRole?.id;
    return (
      <div className="kuika-share__row" data-testid={this.props.testId} key={user.id}>
        <div className="kuika-share__user">
          <KuikaAvatar
            size={28}
            className="kuika-share__avatar"
            src={user.profilePhotoUrl}
            seed={getIdentitySeed(user)}
          >
            {KMainFunctions.getUserLettersToShow(user)}
          </KuikaAvatar>
          <div className="kuika-share__user-text">
            <KuikaTextMedium className="kuika-share__name">{`${user.firstName} ${user.lastName}`}</KuikaTextMedium>
            <span className="kuika-share__email">{user.username}</span>
          </div>
        </div>
        {canEditRole && (
          <KuikaDropdownMenu
            options={this.getRoleMenuOptions()}
            selected={selectedRoleId ?? null}
            overlayClassName="kuika-share__role-menu"
          >
            <button type="button" className="kuika-share__role-trigger">
              <KuikaRoleBadge
                role={this.props.projectRole?.name ?? ""}
                rightIcon={<KuikaSvg name="chevron-down" type="arrows" width={11} height={11} />}
                // The share list keys each member by user, and picking a role from this menu
                // changes it in place — the badge is where the change is confirmed.
                animateChanges
              />
            </button>
          </KuikaDropdownMenu>
        )}
        {isOwner && <KuikaRoleBadge role={this.props.projectRole?.name ?? ""} />}
      </div>
    );
  }
}

export default ProjectDefinitionShareUser;
